/**
 * The layout tabs above the canvas (ADR 0037): one tab per layout ("16:9 · 9:16 · 1:1", the primary first), each with
 * its shape and a count of its problems, and "+" to add a layout. The active tab's menu changes its format or
 * removes it (not the primary). Problems of the other layouts come from an off-screen render of each, made once
 * edits pause.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { ASPECTS, FORMAT_GROUPS, aspectOf, layoutsOf, sceneForLayout, type Scene } from '@prism-graphics/scene-schema';
import { renderStatic, type StaticFrame } from '@prism-graphics/runtime/static';
import { t } from '../i18n.ts';
import { aspectTaken } from '../model/layouts.ts';
import { assetCache } from '../state/assets.ts';
import { activeLayout, useEditor, type EditorStore } from '../state/store.ts';
import { IconButton } from '../ui/controls.tsx';

const IDLE_MS = 700;

/** The first preset of a shape the graphic has no layout for (what "+" suggests). */
export function nextLayoutSize(scene: Scene): { width: number; height: number } | undefined {
  for (const { aspect } of ASPECTS) {
    const g = FORMAT_GROUPS.find((x) => x.aspect === aspect && !aspectTaken(scene, x));
    if (g) return { width: g.width, height: g.height };
  }
  return undefined;
}

const shapeOf = (size: { width: number; height: number }): string => {
  const a = aspectOf(size);
  const shape = ASPECTS.find((x) => x.aspect === a);
  return shape ? t(shape.label) : t('format.shape.other');
};

export function LayoutTabs({ store, activeCount, countProblems }: { store: EditorStore; activeCount: number; countProblems: (scene: Scene, frame: StaticFrame) => number }) {
  const doc = useEditor(store, (s) => s.history.present);
  const active = useEditor(store, activeLayout);
  const testData = useEditor(store, (s) => s.testData);
  const renderState = useEditor(store, (s) => s.ui.renderState);
  const readOnly = useEditor(store, (s) => s.readOnly);
  const assets = useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const layouts = layoutsOf(doc);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [menu, setMenu] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  // The other layouts' problems: an off-screen render of each once edits pause.
  useEffect(() => {
    if (layouts.length < 2) return;
    const timer = setTimeout(() => {
      const host = hostRef.current;
      if (!host) return;
      const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
      const next: Record<string, number> = {};
      for (const l of layoutsOf(doc)) {
        if (l.id === active) continue;
        try {
          const scene = sceneForLayout(doc, l.id);
          host.style.width = `${l.width}px`;
          host.style.height = `${l.height}px`;
          next[l.id] = countProblems(scene, renderStatic(shadow, scene, { families: assetCache.families, imageUrl: assetCache.imageUrl, state: renderState, data: testData }));
        } catch {
          // A layout the runtime cannot show yet: no count.
        }
      }
      setCounts(next);
    }, IDLE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, active, testData, renderState, assets, countProblems]);

  useEffect(() => setMenu(false), [active]);

  const suggested = nextLayoutSize(doc);
  const current = layouts.find((l) => l.id === active) ?? layouts[0]!;
  const isPrimary = current.id === layouts[0]!.id;
  return (
    <div className="layout-tabs" data-testid="layout-tabs">
      <div className="layout-tablist" role="tablist" aria-label={t('layouts.label')}>
      {layouts.map((l, i) => {
        const count = l.id === active ? activeCount : counts[l.id];
        return (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={l.id === active}
            aria-label={t('layouts.tab', { label: l.label })}
            className={`layout-tab${l.id === active ? ' active' : ''}`}
            data-testid="layout-tab"
            data-layout={l.id}
            title={`${l.width} × ${l.height}`}
            onClick={() => store.setLayout(i === 0 ? null : l.id)}
          >
            <span className="layout-label">{l.label}</span>
            <span className="badge muted">{i === 0 ? t('layouts.primary') : shapeOf(l)}</span>
            {count !== undefined && count > 0 && (
              <span className="count" data-testid="layout-problems" title={t('layouts.problems', { count })}>
                {count}
              </span>
            )}
          </button>
        );
      })}
      </div>
      {!readOnly && suggested && (
        <IconButton label={t('layouts.add')} testId="layout-add" onClick={() => store.setUi({ formatDialog: suggested, formatDialogMode: 'add' })}>
          <Plus size={14} />
        </IconButton>
      )}
      {!readOnly && !isPrimary && (
        <div className="layout-menu-anchor">
          <IconButton label={t('layouts.menu', { label: current.label })} testId="layout-menu" pressed={menu} onClick={() => setMenu(!menu)}>
            <MoreHorizontal size={14} />
          </IconButton>
          {menu && (
            <div className="popover layout-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                data-testid="layout-change"
                onClick={() => {
                  setMenu(false);
                  store.setUi({ formatDialog: { width: current.width, height: current.height }, formatDialogMode: 'change' });
                }}
              >
                {t('layouts.change')}
              </button>
              <button
                type="button"
                role="menuitem"
                data-testid="layout-remove"
                onClick={() => {
                  setMenu(false);
                  if (window.confirm(t('layouts.removeConfirm', { label: current.label }))) store.removeLayout(current.id);
                }}
              >
                {t('layouts.remove')}
              </button>
            </div>
          )}
        </div>
      )}
      <div ref={hostRef} className="canvas-offscreen" aria-hidden="true" />
    </div>
  );
}
